import { FC } from "react";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";

import { EnabledCountries, EnabledGenders, EnabledLanguages } from "@dzambalaorg/constants";
import { FormWrapper } from "@dzambalaorg/mui-form";
import { AvatarInput } from "@dzambalaorg/mui-inputs-image-firebase";
import { SelectInput, TextInput } from "@dzambalaorg/mui-inputs-core";
import { useUser } from "@dzambalaorg/provider-user";
import { useApiCall } from "@dzambalaorg/react-hooks";
import { ILanguageStore } from "@dzambalaorg/provider-localization";
import type { IUser } from "@framework/types";
import { useGlobalStore } from "@dzambalaorg/store";
import { FormGrid } from "@dzambalaorg/ui-kit";

import { ITabPanelProps } from "../interfaces";
import { validationSchema } from "./validation";

export const ProfileGeneral = (props: ITabPanelProps) => {
  const { open } = props;

  const user = useUser<IUser>();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const changeLanguage = useGlobalStore(
    state => (state as Record<string, ILanguageStore["changeLanguage"]>).changeLanguage,
  );

  const { fn } = useApiCall((_api, values: Partial<IUser>) => {
    return user.setProfile(values).then(() => {
      if (user.profile.language !== values.language) {
        changeLanguage(values.language!);
      }
    });
  });

  if (!open) {
    return null;
  }

  const onClick = (): void => {
    enqueueSnackbar(formatMessage({ id: "form.hints.emailWarning" }), { variant: "info" });
  };

  const handleSubmit = async (values: Partial<IUser>, form: any): Promise<void> => {
    await fn(form, values);
  };

  const { email, displayName, language, gender, country, imageUrl } = user.profile;

  const fixedValues = {
    email,
    displayName,
    gender: gender ?? "",
    country: country ?? "",
    language,
    imageUrl,
  };

  return (
    <FormWrapper initialValues={fixedValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      <FormGrid
        columns={[2, 3, 3]}
        containerProps={{ sx: theme => ({ marginBottom: theme.spacing(theme.tokens.spacing.xl) }) }}
      >
        <TextInput name="email" autoComplete="off" onClick={onClick} />
        <TextInput name="displayName" />
        <SelectInput name="gender" options={EnabledGenders} />
        <SelectInput name="country" options={EnabledCountries} />
        <SelectInput name="language" options={EnabledLanguages} />
        <AvatarInput name="imageUrl" />
        <FormGrid.Empty />
        <FormGrid.Empty />
      </FormGrid>
    </FormWrapper>
  );
};
